import { useCallback } from 'react';
import axios from 'axios';

import {
	base64ToBinary,
	convertFileToBase64,
	formatBase64,
	compressPdfFile,
	isPdfFile,
} from 'utils';
import { API_URL } from 'constant';
import { envHost } from 'helpers';
import { useNetwork } from 'hooks/network';

export const useFileUpload = () => {
	const { post } = useNetwork(); // Extract the `post` function from a custom network hook

	/**
	 * Function to fetch a pre-signed upload URL for a given file name.
	 * This URL is used to upload the file directly to GCP.
	 */
	const gerUploadUrl = useCallback(
		async (fileName: string) => {
			// Send a POST request to the backend to get the pre-signed URL for the file
			const response = await post(API_URL.PRE_SIGN_UPLOAD, {
				filename: fileName || '', // Ensure an empty string is passed if fileName is undefined
			});

			// Return the response or null if no response is received
			return response ?? null;
		},
		[post] // Dependency on the `post` function
	);

	/**
	 * Function to upload a file to GCP using a pre-signed URL.
	 * Converts the file to binary before sending it to GCP.
	 */
	const uploadOnGcp = useCallback(
		/**
		 * `apiDomain` is the value the presign endpoint SIGNED the url with, passed
		 * back here so this PUT echoes it exactly. It used to be derived locally
		 * from `envHost`, which knows stage/pp/prod/beta and defaults to `stage` --
		 * so on dev2 the browser sent `stage` against a url signed for something
		 * else and GCS rejected the whole request with 403 SignatureDoesNotMatch.
		 * Deriving a signed value on both sides can only ever agree by luck.
		 * The `envHost` fallback keeps older servers (which do not return it)
		 * behaving exactly as before.
		 */
		async (url: string, file: File, apiDomain?: string) => {
			try {
				// Step 1: Convert the file to Base64
				const base64 = await convertFileToBase64(file);
				if (!base64) throw new Error('Failed to convert file to Base64.');

				// Step 2: Format the Base64 string (e.g., remove metadata)
				const formattedBase64 = formatBase64(base64 as string);
				if (!formattedBase64) throw new Error('Failed to format Base64 data.');

				// Step 3: Convert the formatted Base64 string to binary (Uint8Array)
				const fileBinary = base64ToBinary(formattedBase64);
				if (!fileBinary) throw new Error('Failed to convert Base64 to binary.');

				// Step 4: Send a PUT request to the pre-signed URL with the binary data
				const response = await axios.put(url, fileBinary, {
					headers: {
						'Content-Type': file?.type ?? '', // MIME type of the file
						'x-goog-meta-presignedupload': 'true', // Metadata indicating a presigned upload
						'x-goog-meta-apiDomain': apiDomain || envHost,
					},
				});

				// If the upload is successful, return a success response
				if (response.status === 200) {
					return { success: true };
				}

				// Throw an error if the response is not successful
				throw new Error('Failed to upload the file to GCP.');
			} catch (error) {
				// Return null if any error occurs during the process
				return null;
			}
		},
		[] // No dependencies required
	);

		/**
	 * Main function to handle document upload:
	 * 1. Compresses PDFs before upload to keep fonts readable and images visible.
	 * 2. Fetches the pre-signed upload URL from the backend.
	 * 3. Uploads the file (compressed if applicable) to GCP using the pre-signed URL.
	 * 4. Returns the document ID if successful.
	 */
	const uploadDoc = useCallback(
		async (file: File) => {
			let fileToUpload: File = file;

			// Compress PDF before upload
			if (isPdfFile(file)) {
				const compressed = await compressPdfFile(file);
				if (compressed) {
					fileToUpload = compressed;
				}
			}
			// Step 1: Get the pre-signed URL for the file upload (before upload)
			const uploadResp = await gerUploadUrl(fileToUpload?.name ?? '');

			const { uploadUrl, documentId, message, apiDomain } = uploadResp ?? {};

			// Step 2: Upload the file (compressed or original) to GCP
			if (uploadUrl) {
				const resp = await uploadOnGcp(uploadUrl, fileToUpload, apiDomain);
				const result = resp ? { ...resp, documentId, message } : resp;
				return result;
			}

			return message ? { success: false, documentId: '', message } : null;
		},
		[gerUploadUrl, uploadOnGcp]
	);

	// Return the main document upload function for external use
	return { uploadDoc };
};
